import classNames from 'classnames';
import { observer } from 'mobx-react';
import React from 'react';
import ReactDOM from 'react-dom';
import { FocusCycle, forceTabOrder, KeyStrings } from '@udemy/design-system-utils';
import { useMatchMedia } from '@udemy/hooks';
import { useI18n } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { BlockList } from '@udemy/react-core-components';
import { menuAim, MenuAimArea } from '@udemy/react-popup-components';
import { ProBadge } from '@udemy/react-pro-content';
import { SubscriptionFeatureKey, useSubscriptionFeatures, withSubscriptionFeaturesProvider } from '@udemy/react-subscription-features-provider';
import { noop, range } from '@udemy/shared-utils';
import { mediaQueryPxToRem } from '@udemy/styles';
import { useUDData } from '@udemy/ud-data';
import { AGGREGATOR_NAV, ACADEMIES_NAV, AI_ASSISTANT_NAV, BADGING_NAV, BROWSE_LEARNING_TYPES, ROLE_PLAY_NAV, AI_ELEVATE_NAV, GOOGLE_CERTIFICATE_NAV } from '../../browse-constants';
import { AggregatorNavItemType, AcademiesNavItemType, GoogleCertificateNavItemType } from '../../events';
import { getBrowseURL } from '../../get-browse-url';
import { useAILearningNav } from '../../hooks/use-ai-learning-nav';
import { getUserTypes } from '../../utils/get-user-types';
import { forceHover } from '../header-dropdown.react-component';
import menuStyles from "../list-menu.module.css";
import styles from "./browse-nav.module.css";
import { DesktopNavSectionHeading } from './desktop-nav.react-component';
export class BrowseNavItemFinders {
  constructor() {
    this.findSelectedLevelOneItem = browseNavStore => {
      const item = browseNavStore.selectedLevelOneItem;
      if (!item) {
        return null;
      }
      return document.querySelector(`.js-browse-nav-level-one [data-id="${item.id}"]`);
    };
    this.findSelectedLevelTwoItem = browseNavStore => {
      const item = browseNavStore.selectedLevelTwoItem;
      if (!item) {
        return null;
      }
      return document.querySelector(`.js-browse-nav-level-two [data-id="${item.id}"]`);
    };
    this.findFirstLevelTwoItem = () => {
      return document.querySelector('.js-browse-nav-level-two [data-id]');
    };
    this.findLastLevelTwoItem = () => {
      const items = document.querySelectorAll('.js-browse-nav-level-two [data-id]');
      return items ? items[items.length - 1] : null;
    };
    this.findFirstLevelThreeItem = () => {
      return document.querySelector('.js-browse-nav-level-three [data-id]');
    };
    this.findLastLevelThreeItem = () => {
      const items = document.querySelectorAll('.js-browse-nav-level-three [data-id]');
      return items ? items[items.length - 1] : null;
    };
  }
}
const BrowseNavInternal = observer(({
  browseNavStore,
  headerStore,
  ufbContext
}) => {
  var _useMatchMedia, _headerStore$userSpec, _headerStore$userSpec2, _headerStore$userSpec3, _headerStore$userSpec4, _headerStore$userSpec5, _headerStore$userSpec6, _headerStore$userSpec7, _headerStore$userSpec8, _headerStore$userSpec9, _headerStore$userSpec0, _headerStore$userSpec1;
  const {
    Config,
    me
  } = useUDData();
  const {
    gettext
  } = useI18n();
  const canFitLevelThree = (_useMatchMedia = useMatchMedia(`(min-width: ${mediaQueryPxToRem(930)}rem)`)) !== null && _useMatchMedia !== void 0 ? _useMatchMedia : true;
  const ref = React.useRef(null);
  const [disposeForceTabOrder, setDisposeForceTabOrder] = React.useState();
  const [itemFinders] = React.useState(new BrowseNavItemFinders());
  const {
    aiLearningNav,
    showLearnAINavigation
  } = useAILearningNav();

  // User plan type flags from header data (no hook – use helper directly)
  const {
    isUBUser,
    isUBProUser,
    isPPUser,
    isMXUser,
    isTPUser,
    isConsumerSubscriber
  } = getUserTypes({
    config: Config,
    me,
    headerData: headerStore.userSpecificContext
  });
  // Only show the Google Certificate entry points if the user is not a UB user
  // and has access to Grow with Google subscription plan
  const showGoogleCertificateEntryPoints = headerStore.isGrowWithGoogleNavigationEnabled && !isUBUser;
  const showPPLearningTypesMenu = (_headerStore$userSpec = headerStore.userSpecificContext.user) === null || _headerStore$userSpec === void 0 ? void 0 : _headerStore$userSpec.enableLabsInPersonalPlan;
  const showUpdatedPPAndUBNavigation = (_headerStore$userSpec2 = headerStore.userSpecificContext.user) === null || _headerStore$userSpec2 === void 0 ? void 0 : _headerStore$userSpec2.show_updated_pp_and_ub_navigation;
  const showUpdatedMXNavigation = (_headerStore$userSpec3 = headerStore.userSpecificContext.user) === null || _headerStore$userSpec3 === void 0 ? void 0 : _headerStore$userSpec3.show_updated_mx_navigation;
  const showLearningTypesMenu = isPPUser && showPPLearningTypesMenu || isUBProUser && showUpdatedPPAndUBNavigation;
  const showCategoriesNavForUB = (_headerStore$userSpec4 = headerStore.userSpecificContext.user) === null || _headerStore$userSpec4 === void 0 ? void 0 : _headerStore$userSpec4.show_category_navigation_for_ub_users;
  const showCertPrepEntryPointForUB = (_headerStore$userSpec5 = headerStore.userSpecificContext.user) === null || _headerStore$userSpec5 === void 0 ? void 0 : _headerStore$userSpec5.show_certificate_content_for_ub_users;
  const showAIElevateEntryPointForUB = (_headerStore$userSpec6 = headerStore.userSpecificContext.user) === null || _headerStore$userSpec6 === void 0 ? void 0 : _headerStore$userSpec6.show_ai_elevate_for_ub_users;
  const shouldEnableAIAssistant = (_headerStore$userSpec7 = headerStore.userSpecificContext.user) === null || _headerStore$userSpec7 === void 0 ? void 0 : _headerStore$userSpec7.show_ai_assistant;
  const showContentDiscoveryAIAssistant = (_headerStore$userSpec8 = headerStore.userSpecificContext.user) === null || _headerStore$userSpec8 === void 0 ? void 0 : _headerStore$userSpec8.show_content_discovery_ai_assistant_available;

  // Aggregator menus are shown if user is not a PP user, not a UB user, and the aggregator experiment is enabled.
  const isAggregatorEnabled = headerStore.isAggregatorFeatureEnabled && isMXUser;
  const showLearnerOrganizationBadges = (_headerStore$userSpec9 = headerStore.userSpecificContext.user) === null || _headerStore$userSpec9 === void 0 ? void 0 : _headerStore$userSpec9.show_learner_organization_badges;
  // Only show the academies entry points if the user is not a PP user or a UB user.
  const showAcademiesEntryPoints = headerStore.isAcademiesFeatureEnabled && !isUBUser && !!((_headerStore$userSpec0 = headerStore.userSpecificContext.careerAcceleratorsNav) !== null && _headerStore$userSpec0 !== void 0 && _headerStore$userSpec0.length);
  const showBadgesMenu = showUpdatedPPAndUBNavigation || showUpdatedMXNavigation || isAggregatorEnabled;
  const showGoalsMenu = showBadgesMenu || showAcademiesEntryPoints || showLearnAINavigation || headerStore.isRolePlayFeatureEnabled;
  const subscriptionFeaturesResult = useSubscriptionFeatures([SubscriptionFeatureKey.Assessments]);
  const hasAssessmentsSubscriptionFeature = Boolean(subscriptionFeaturesResult.subscriptionFeatures.get(SubscriptionFeatureKey.Assessments));
  const shouldRenderAssessmentsInGoalsSection = ((_headerStore$userSpec1 = headerStore.userSpecificContext.user) === null || _headerStore$userSpec1 === void 0 ? void 0 : _headerStore$userSpec1.enableAssessmentsInPersonalPlan) || hasAssessmentsSubscriptionFeature;
  const showAIElevateEntryPoint = isConsumerSubscriber && showAIElevateEntryPointForUB;
  const hideCategoryNavigationMenu = isConsumerSubscriber && !showCategoriesNavForUB;
  const hideCertPrepEntryPoint = isConsumerSubscriber && !showCertPrepEntryPointForUB;
  const showSplitCategoriesNav = headerStore.isSplitNavByCategoriesEnabled && isMXUser;

  // Item finders
  const findSelectedLevelOneItem = () => itemFinders.findSelectedLevelOneItem(browseNavStore);
  const findSelectedLevelTwoItem = () => itemFinders.findSelectedLevelTwoItem(browseNavStore);
  const findFirstLevelTwoItem = () => itemFinders.findFirstLevelTwoItem();
  const findLastLevelTwoItem = itemFinders.findLastLevelTwoItem;
  const findFirstLevelThreeItem = itemFinders.findFirstLevelThreeItem;
  const findLastLevelThreeItem = itemFinders.findLastLevelThreeItem;
  React.useEffect(() => {
    // - If you TAB on the selected level N item, you go to level N + 1.
    // - If you ESC on level N + 1, you go back to level N.
    const dispose = forceTabOrder([[findSelectedLevelOneItem, findFirstLevelTwoItem], [findSelectedLevelTwoItem, findFirstLevelThreeItem], [findLastLevelThreeItem, findSelectedLevelTwoItem], [findLastLevelTwoItem, findSelectedLevelOneItem]]);
    setDisposeForceTabOrder(dispose);
    const currentRef = ref.current;
    // We use event.stopPropagation when you ESC on sub-navs to prevent Popover from
    // closing entirely; we want only the sub-nav to close.
    // The stopPropagation works only via addEventListener, not via React's onKeyUp.
    currentRef === null || currentRef === void 0 || currentRef.addEventListener('keyup', onKeyUp);
    return () => {
      disposeForceTabOrder === null || disposeForceTabOrder === void 0 || disposeForceTabOrder();
      currentRef === null || currentRef === void 0 || currentRef.removeEventListener('keyup', onKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  function onKeyUp(event) {
    if (event.key === KeyStrings.ESCAPE || event.key === KeyStrings.LEFT) {
      if (browseNavStore.selectedLevelTwoItem) {
        var _findSelectedLevelTwo;
        // Deselect the selected level two item on ESCAPE.
        event.stopPropagation();
        (_findSelectedLevelTwo = findSelectedLevelTwoItem()) === null || _findSelectedLevelTwo === void 0 || _findSelectedLevelTwo.focus();
        browseNavStore.selectLevelTwoItem(browseNavStore.selectedLevelTwoItem);
      } else if (browseNavStore.selectedLevelOneItem) {
        var _findSelectedLevelOne;
        // Deselect the selected level one item on ESCAPE.
        event.stopPropagation();
        (_findSelectedLevelOne = findSelectedLevelOneItem()) === null || _findSelectedLevelOne === void 0 || _findSelectedLevelOne.focus();
        browseNavStore.selectLevelOneItem(browseNavStore.selectedLevelOneItem);
      }
    }
  }
  function renderLevelOneNavItem(item) {
    return /*#__PURE__*/React.createElement(BrowseNavItem, Object.assign({
      navItem: item,
      key: item.id,
      onSelect: browseNavStore.selectLevelOneItem
    }, item.absolute_url !== 'none' && {
      href: item.absolute_url
    }, {
      id: `header-browse-nav-${item.type}-${item.id}`,
      isLearningTypeNavItem: true,
      isSelected: item === browseNavStore.selectedLevelOneItem
    }));
  }
  function renderAIAssistantMenu() {
    const aiAssistantItem = AI_ASSISTANT_NAV(gettext).AI_ASSISTANT;
    const aiAssistantNavItem = {
      ...aiAssistantItem,
      title: /*#__PURE__*/React.createElement("div", {
        className: styles['ai-assistant-nav-item']
      }, /*#__PURE__*/React.createElement(Icon, {
        glyph: "sparkles",
        size: 'xsmall',
        color: "info",
        label: false
      }), /*#__PURE__*/React.createElement("span", null, aiAssistantItem.title))
    };
    const aiElevateItem = AI_ELEVATE_NAV(gettext, isTPUser).AI_COURSES;
    const aiElevateNavItem = {
      ...aiElevateItem,
      title: /*#__PURE__*/React.createElement("div", {
        className: styles['ai-courses-nav-item']
      }, /*#__PURE__*/React.createElement("span", null, aiElevateItem.title))
    };
    return /*#__PURE__*/React.createElement(React.Fragment, null, showAIElevateEntryPoint && /*#__PURE__*/React.createElement(DesktopNavSectionHeading, null, gettext('Learn AI')), /*#__PURE__*/React.createElement(BlockList, {
      size: "small",
      className: classNames(menuStyles['section'], {
        [menuStyles['section-without-heading']]: !showAIElevateEntryPoint
      })
    }, showAIElevateEntryPoint && renderLevelOneNavItem(aiElevateNavItem), shouldEnableAIAssistant && showContentDiscoveryAIAssistant && renderLevelOneNavItem(aiAssistantNavItem)));
  }
  function renderLearningTypesMenu() {
    var _headerStore$userSpec10;
    let headingText;
    if (isUBProUser) {
      headingText = gettext('Explore Pro');
    } else {
      headingText = showUpdatedPPAndUBNavigation ? gettext('Explore active learning') : gettext('Explore by type');
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DesktopNavSectionHeading, null, headingText, isUBProUser && /*#__PURE__*/React.createElement(ProBadge, {
      "aria-hidden": "true"
    })), /*#__PURE__*/React.createElement(BlockList, {
      size: "small",
      className: menuStyles['section']
    }, (((_headerStore$userSpec10 = headerStore.userSpecificContext.user) === null || _headerStore$userSpec10 === void 0 ? void 0 : _headerStore$userSpec10.enableLabsInPersonalPlan) || isUBProUser) && renderLevelOneNavItem(BROWSE_LEARNING_TYPES(gettext).LABS), isUBProUser && renderLevelOneNavItem(BROWSE_LEARNING_TYPES(gettext).UDEMY_PRO_PATHS)));
  }
  function renderGoogleCertificateMenu() {
    const googleCertificateNavItem = GOOGLE_CERTIFICATE_NAV(gettext).GOOGLE_CERTIFICATES;

    // Build the URL for each accelerator using the slug
    // Use the absolute_url from the backend, or fallback to constructing from slug
    const gwgAcceleratorItems = (headerStore.userSpecificContext.gwgAcceleratorsNav || []).map((accelerator, index) => ({
      id: index + 1,
      type: GoogleCertificateNavItemType.GOOGLE_CERTIFICATE_PATH,
      title: accelerator.title,
      absolute_url: accelerator.absolute_url || `/grow-with-google/${accelerator.slug}/`
    }));
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DesktopNavSectionHeading, null, gettext('New & Featured')), /*#__PURE__*/React.createElement(BlockList, {
      size: "small",
      className: menuStyles['section'],
      iconAlignment: "right"
    }, /*#__PURE__*/React.createElement(BrowseNavItem, {
      navItem: {
        ...googleCertificateNavItem,
        title: /*#__PURE__*/React.createElement("div", {
          className: styles['google-certificate-nav-item']
        }, /*#__PURE__*/React.createElement(Icon, {
          size: "xxxsmall",
          glyph: "google",
          label: ""
        }), /*#__PURE__*/React.createElement("span", null, googleCertificateNavItem.title)),
        children: gwgAcceleratorItems
      },
      key: googleCertificateNavItem.id,
      onSelect: browseNavStore.selectLevelOneItem,
      findFirstSubNavItem: findFirstLevelTwoItem,
      isSelected: googleCertificateNavItem === browseNavStore.selectedLevelOneItem,
      subNavId: "header-browse-nav-level-two",
      id: `header-browse-nav-${googleCertificateNavItem.type}-${googleCertificateNavItem.id}`
    })));
  }
  function renderGoalsMenu() {
    // Hide Goals section for UB China
    if (Config.brand.organization && Config.brand.organization.is_enterprise_china) {
      return null;
    }
    const badgeNavItem = browseNavStore.aggregatorNavCategory;
    const academiesNavItem = ACADEMIES_NAV(gettext).ALL_ACADEMIES;
    const rolePlayNavItem = ROLE_PLAY_NAV(gettext).ROLE_PLAY;
    let isExploreByGoalHeadingTitleShown = false;
    let sectionHeading = isAggregatorEnabled ? gettext('Browse Certifications') : gettext('Explore badges');
    if (showAcademiesEntryPoints || showLearnAINavigation || headerStore.isRolePlayFeatureEnabled) {
      isExploreByGoalHeadingTitleShown = true;
      badgeNavItem.title = gettext('Prepare for a certification');
      sectionHeading = gettext('Explore by Goal');
    }
    const showElementsForUB = !isAggregatorEnabled && showBadgesMenu;
    if (showSplitCategoriesNav) {
      rolePlayNavItem.title = gettext('Courses with Role Play');
      academiesNavItem.title = gettext('In-demand careers');
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, !showSplitCategoriesNav && /*#__PURE__*/React.createElement(DesktopNavSectionHeading, null, sectionHeading), /*#__PURE__*/React.createElement(BlockList, {
      size: "small",
      className: menuStyles['section'],
      iconAlignment: "right"
    }, showLearnAINavigation && /*#__PURE__*/React.createElement(BrowseNavItem, {
      navItem: aiLearningNav.AI_LEARNING,
      key: aiLearningNav.AI_LEARNING.id,
      onSelect: browseNavStore.selectLevelOneItem,
      findFirstSubNavItem: findFirstLevelTwoItem,
      isSelected: aiLearningNav.AI_LEARNING === browseNavStore.selectedLevelOneItem,
      subNavId: "header-browse-nav-level-two",
      id: `header-browse-nav-${aiLearningNav.AI_LEARNING.type}-${aiLearningNav.AI_LEARNING.id}`,
      color: "link",
      componentClass: "button",
      isLearningAiTypeNavItem: true
    }), (showAcademiesEntryPoints || showSplitCategoriesNav) && /*#__PURE__*/React.createElement(BrowseNavItem, {
      navItem: academiesNavItem,
      key: `${academiesNavItem.type}-${academiesNavItem.id}`,
      onSelect: browseNavStore.selectLevelOneItem,
      href: academiesNavItem.absolute_url,
      findFirstSubNavItem: findFirstLevelTwoItem,
      id: `header-browse-nav-${academiesNavItem.type}-${academiesNavItem.id}`,
      subNavId: "header-browse-nav-level-two",
      isSelected: academiesNavItem === browseNavStore.selectedLevelOneItem
    }), isAggregatorEnabled && !showSplitCategoriesNav && /*#__PURE__*/React.createElement(BrowseNavItem, {
      href: badgeNavItem.absolute_url,
      key: badgeNavItem.id,
      navItem: badgeNavItem,
      onSelect: browseNavStore.selectLevelOneItem,
      findFirstSubNavItem: findFirstLevelTwoItem,
      isSelected: badgeNavItem == browseNavStore.selectedLevelOneItem,
      subNavId: "header-browse-nav-level-two",
      id: `header-browse-nav-${badgeNavItem.type}-${badgeNavItem.id}`
    }), !hideCertPrepEntryPoint && showElementsForUB && !showSplitCategoriesNav && renderLevelOneNavItem(BADGING_NAV(gettext, isExploreByGoalHeadingTitleShown).CERTIFICATIONS), showElementsForUB && showLearnerOrganizationBadges && renderLevelOneNavItem(BADGING_NAV(gettext, isExploreByGoalHeadingTitleShown).ORGANIZATION_BADGES), shouldRenderAssessmentsInGoalsSection && renderLevelOneNavItem(BADGING_NAV(gettext).ASSESSMENTS_NAV), (showSplitCategoriesNav || headerStore.isRolePlayFeatureEnabled) && /*#__PURE__*/React.createElement(BrowseNavItem, {
      href: rolePlayNavItem.absolute_url,
      key: `${rolePlayNavItem.type}-${rolePlayNavItem.id}`,
      navItem: rolePlayNavItem,
      onSelect: browseNavStore.selectLevelOneItem,
      isSelected: rolePlayNavItem == browseNavStore.selectedLevelOneItem,
      isLearningTypeNavItem: true,
      id: `header-browse-nav-${rolePlayNavItem.type}-${rolePlayNavItem.id}`
    })));
  }
  function renderCategoriesMenu() {
    var _browseNavStore$navig;
    const showHeading = isPPUser || isUBUser && !isTPUser || isTPUser && showUpdatedPPAndUBNavigation || showUpdatedMXNavigation;
    const showUpdatedCategoriesHeading = isPPUser || showUpdatedPPAndUBNavigation || showUpdatedMXNavigation;
    const hasBrowseNavigationCategories = ((_browseNavStore$navig = browseNavStore.navigationCategories) === null || _browseNavStore$navig === void 0 ? void 0 : _browseNavStore$navig.length) > 0;

    // Hide categories menu for UB users when the category navigation feature is not enabled
    if (hideCategoryNavigationMenu) {
      return null;
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, showHeading && /*#__PURE__*/React.createElement(DesktopNavSectionHeading, null, showUpdatedCategoriesHeading ? gettext('Explore by category') : gettext('All categories')), /*#__PURE__*/React.createElement(BlockList, {
      size: "small",
      className: menuStyles['section'],
      iconAlignment: "right"
    }, hasBrowseNavigationCategories ? browseNavStore.navigationCategories.map(category => /*#__PURE__*/React.createElement(BrowseNavItem, {
      key: category.id,
      navItem: category,
      onSelect: browseNavStore.selectLevelOneItem,
      findFirstSubNavItem: findFirstLevelTwoItem,
      isSelected: category === browseNavStore.selectedLevelOneItem,
      subNavId: "header-browse-nav-level-two",
      id: `header-browse-nav-${category.type}-${category.id}`
    })) : range(13).map(i => /*#__PURE__*/React.createElement(BlockList.Item, {
      key: i,
      loading: true,
      className: menuStyles['item']
    }))));
  }
  function renderLevelOneNav() {
    var _ufbContext$getBrowse;
    return /*#__PURE__*/React.createElement("div", {
      className: `js-browse-nav-level-one ${styles['nav']}`,
      "data-testid": "browse-nav-list"
    }, (shouldEnableAIAssistant && showContentDiscoveryAIAssistant || showAIElevateEntryPoint) && renderAIAssistantMenu(), showGoogleCertificateEntryPoints && renderGoogleCertificateMenu(), showGoalsMenu && renderGoalsMenu(), showLearningTypesMenu && renderLearningTypesMenu(), ufbContext === null || ufbContext === void 0 || (_ufbContext$getBrowse = ufbContext.getBrowseNavLevelOneItems) === null || _ufbContext$getBrowse === void 0 ? void 0 : _ufbContext$getBrowse.call(ufbContext, itemFinders), renderCategoriesMenu());
  }
  function renderSubcategoryItems(items, isAILearning) {
    return /*#__PURE__*/React.createElement(BlockList, {
      size: "small",
      className: menuStyles['section'],
      iconAlignment: "right"
    }, items.length ? items === null || items === void 0 ? void 0 : items.map(item => /*#__PURE__*/React.createElement(BrowseNavItem, Object.assign({
      key: item.id,
      navItem: item,
      onSelect: browseNavStore.selectLevelTwoItem,
      isSelected: item === browseNavStore.selectedLevelTwoItem,
      findFirstSubNavItem: findFirstLevelThreeItem,
      subNavId: canFitLevelThree ? 'header-browse-nav-level-three' : undefined,
      id: `header-browse-nav-${item.type}-${item.id}`
    }, isAILearning && {
      color: 'link',
      componentClass: 'button'
    }))) : range(8).map(i => /*#__PURE__*/React.createElement(BlockList.Item, {
      key: i,
      loading: true,
      className: menuStyles['item']
    })));
  }
  function renderSubcategoryNav() {
    let labelledbyId;
    let content = null;
    const parentItem = browseNavStore.selectedLevelOneItem;

    // We don't want to show the subcategory nav for certain parent items
    const isExcludedParentItemType = Boolean(parentItem) && [...Object.values(BADGING_NAV(gettext)).map(item => item.type), ...Object.values(BROWSE_LEARNING_TYPES(gettext)).map(item => item.type)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ].includes(parentItem === null || parentItem === void 0 ? void 0 : parentItem.type);
    const isAggregatorParentItemType = Boolean(parentItem) && [...Object.values(AGGREGATOR_NAV(gettext)).map(item => item.type)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ].includes(parentItem === null || parentItem === void 0 ? void 0 : parentItem.type);
    const isAcademiesParentItemType = Boolean(parentItem) && [...Object.values(ACADEMIES_NAV(gettext)).map(item => item.type)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ].includes(parentItem === null || parentItem === void 0 ? void 0 : parentItem.type);
    const isAILearningParentItemType = Boolean(parentItem) && [...Object.values(aiLearningNav).map(item => item.type)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ].includes(parentItem === null || parentItem === void 0 ? void 0 : parentItem.type);
    const isGoogleCertificateParentItemType = Boolean(parentItem) && [...Object.values(GOOGLE_CERTIFICATE_NAV(gettext)).map(item => item.type)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ].includes(parentItem === null || parentItem === void 0 ? void 0 : parentItem.type);
    if (parentItem && isGoogleCertificateParentItemType && showGoogleCertificateEntryPoints) {
      labelledbyId = `header-browse-nav-${parentItem.type}-${parentItem.id}`;
      // Get the children that were dynamically set on the parent item
      const googleCertificatePaths = parentItem.children || [];
      content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DesktopNavSectionHeading, null, gettext('Google Learning Paths')), /*#__PURE__*/React.createElement(BlockList, {
        size: "small",
        className: menuStyles['section'],
        iconAlignment: "right"
      }, googleCertificatePaths.map(item => /*#__PURE__*/React.createElement(BrowseNavItem, {
        key: item.id,
        navItem: item,
        onSelect: browseNavStore.selectLevelTwoItem,
        isSelected: item === browseNavStore.selectedLevelTwoItem,
        id: `header-browse-nav-${item.type}-${item.id}`
      }))));
    } else if (parentItem && isAggregatorParentItemType && isAggregatorEnabled) {
      labelledbyId = `header-browse-nav-${parentItem.type}-${parentItem.id}`;
      content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DesktopNavSectionHeading, null, gettext('Popular Issuers')), renderSubcategoryItems(browseNavStore.aggregatorIssuers), /*#__PURE__*/React.createElement(DesktopNavSectionHeading, null, gettext('Popular Subjects')), renderSubcategoryItems(browseNavStore.aggregatorSubjects));
    } else if (parentItem && isAcademiesParentItemType && showAcademiesEntryPoints) {
      labelledbyId = `header-browse-nav-${parentItem.type}-${parentItem.id}`;
      content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DesktopNavSectionHeading, null, gettext('In-demand Careers')), /*#__PURE__*/React.createElement(BlockList, {
        size: "small",
        className: menuStyles['section'],
        iconAlignment: "right"
      }, headerStore.userSpecificContext.careerAcceleratorsNav.map(item => /*#__PURE__*/React.createElement(BrowseNavItem, {
        key: item.id,
        navItem: item,
        onSelect: browseNavStore.selectLevelTwoItem,
        isSelected: item === browseNavStore.selectedLevelTwoItem,
        id: `header-browse-nav-${item.type}-${item.id}`
      }))));
    } else if (parentItem && isAILearningParentItemType && showLearnAINavigation) {
      labelledbyId = `header-browse-nav-${parentItem.type}-${parentItem.id}`;
      content = parentItem.children && renderSubcategoryItems(parentItem.children, true);
    } else if (parentItem && !isExcludedParentItemType) {
      var _ufbContext$getBrowse2;
      labelledbyId = `header-browse-nav-${parentItem.type}-${parentItem.id}`;
      const ufbContent = ufbContext === null || ufbContext === void 0 || (_ufbContext$getBrowse2 = ufbContext.getBrowseNavLevelTwoItems) === null || _ufbContext$getBrowse2 === void 0 ? void 0 : _ufbContext$getBrowse2.call(ufbContext, parentItem);
      content = ufbContent || parentItem.children && renderSubcategoryItems(parentItem.children);
    }
    return /*#__PURE__*/React.createElement(FocusCycle, null, /*#__PURE__*/React.createElement("div", {
      "aria-labelledby": labelledbyId,
      id: "header-browse-nav-level-two",
      className: `js-browse-nav-level-two ${styles['nav']}`,
      style: {
        display: content ? 'block' : 'none'
      },
      "data-testid": "browse-nav-list"
    }, content));
  }
  function renderTopicNav() {
    if (!canFitLevelThree) {
      return null;
    }
    let labelledbyId;
    let heading = null;
    let content = null;
    const parentItem = browseNavStore.selectedLevelTwoItem;
    const isAggregatorParentItemType = (parentItem === null || parentItem === void 0 ? void 0 : parentItem.type) === AggregatorNavItemType.CERTIFICATION_ISSUER_AGGREGATOR || (parentItem === null || parentItem === void 0 ? void 0 : parentItem.type) === AggregatorNavItemType.CERTIFICATION_SUBJECT_AGGREGATOR;
    const isAcademiesParentItemType = (parentItem === null || parentItem === void 0 ? void 0 : parentItem.type) === AcademiesNavItemType.CAREER_ACADEMY || (parentItem === null || parentItem === void 0 ? void 0 : parentItem.type) === AcademiesNavItemType.ALL_ACADEMIES;
    const isGoogleCertificatePathType = (parentItem === null || parentItem === void 0 ? void 0 : parentItem.type) === GoogleCertificateNavItemType.GOOGLE_CERTIFICATE_PATH;
    // Don't render level three for Academies or Google Certificate paths (they are terminal)
    if (parentItem && !isAcademiesParentItemType && !isGoogleCertificatePathType) {
      const topics = isAggregatorParentItemType && isAggregatorEnabled ? parentItem.children : browseNavStore.getTopics(parentItem);
      labelledbyId = `header-browse-nav-${parentItem.type}-${parentItem.id}`;
      heading = /*#__PURE__*/React.createElement(DesktopNavSectionHeading, null, gettext('Popular topics'));
      content = /*#__PURE__*/React.createElement(BlockList, {
        size: "small",
        className: menuStyles['section'],
        iconAlignment: "right"
      }, topics ? topics.map(topic => /*#__PURE__*/React.createElement(BrowseNavItem, {
        onSelect: browseNavStore.selectLevelThreeItem,
        key: topic.id,
        navItem: topic
      })) : range(9).map(i => /*#__PURE__*/React.createElement(BlockList.Item, {
        key: i,
        loading: true,
        className: menuStyles['item']
      })));
    }
    return /*#__PURE__*/React.createElement(FocusCycle, null, /*#__PURE__*/React.createElement("div", {
      "aria-labelledby": labelledbyId,
      id: "header-browse-nav-level-three",
      className: `js-browse-nav-level-three ${styles['nav']}`,
      style: {
        display: content ? 'block' : 'none'
      },
      "data-testid": "browse-nav-list"
    }, !isAggregatorParentItemType && heading, content));
  }
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: `${menuStyles['list-menu-container']} ${styles['nav-container']}`,
    "data-testid": "browse-nav"
  }, renderLevelOneNav(), renderSubcategoryNav(), renderTopicNav());
});
export const BrowseNav = withSubscriptionFeaturesProvider([SubscriptionFeatureKey.Assessments], BrowseNavInternal);
const InternalBrowseNavItem = /*#__PURE__*/React.forwardRef(({
  navItem,
  isSelected,
  subNavId,
  isLearningTypeNavItem,
  isLearningAiTypeNavItem,
  onSelect,
  findFirstSubNavItem,
  onMenuAimUpdate = noop,
  ...blockListItemProps
}, ref) => {
  const {
    Config
  } = useUDData();
  const [selectedVia, setSelectedVia] = React.useState();
  const itemRef = React.useRef(null);
  const getMenuNode = React.useCallback(() => {
    if (subNavId) {
      return document.getElementById(subNavId);
    }
    return null;
  }, [subNavId]);
  React.useImperativeHandle(ref, () => {
    return {
      getTriggerNode: () => itemRef.current,
      onMouseLeave: mousePos => forceHover(`.${menuStyles.item}`, mousePos),
      getMenuNode
    };
  }, [getMenuNode]);
  function focusOnFirstSubNavItem() {
    const firstSubNavItem = findFirstSubNavItem === null || findFirstSubNavItem === void 0 ? void 0 : findFirstSubNavItem();
    firstSubNavItem && firstSubNavItem.focus();
  }
  function onClick(event) {
    if (selectedVia === 'keyboard' && subNavId) {
      // See HeaderButton.onClick in header-dropdown.react-component.js.
      event.preventDefault();
    }
    setSelectedVia('click');
    onSelect === null || onSelect === void 0 || onSelect(navItem, {
      toggle: false,
      selectedVia: 'click'
    });
  }
  function onKeyDown(event) {
    if (event.key === KeyStrings.SPACE || event.key === KeyStrings.RIGHT) {
      event.preventDefault();
      setSelectedVia('keyboard');
      onSelect === null || onSelect === void 0 || onSelect(navItem, {
        toggle: event.key !== KeyStrings.RIGHT,
        selectedVia: 'keyboard'
      });
      if (findFirstSubNavItem) {
        setTimeout(focusOnFirstSubNavItem, 0);
      }
    }
  }
  function onMouseOver(event) {
    if (!isSelected) {
      setSelectedVia('mouse');
      onSelect === null || onSelect === void 0 || onSelect(navItem, {
        toggle: false,
        selectedVia: 'mouse'
      });
      const target = event.target;
      if (target.tagName && target.tagName.toLowerCase() !== 'path') {
        // Ignore mouseover events on MenuAimSVG.
        setTimeout(onMenuAimUpdate, 0); // Wait for menu to render.
      }
    }
  }
  function renderAimArea() {
    const container = getMenuNode();
    return container && /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/React.createElement(MenuAimArea, null), container);
  }
  const enableMouseOver = subNavId || isLearningTypeNavItem;

  // We want onClick for a11y, not onFocus.
  /* eslint-disable jsx-a11y/mouse-events-have-key-events */
  return /*#__PURE__*/React.createElement(BlockList.Item, Object.assign({
    ref: itemRef.current ? itemRef : null
  }, navItem.absolute_url !== 'none' && {
    href: getBrowseURL(navItem.absolute_url, Config.brand.has_organization, isLearningAiTypeNavItem)
  }, {
    "data-testid": "browse-nav-item"
  }, blockListItemProps, {
    "data-id": navItem.id,
    className: menuStyles['item'],
    color: subNavId && isSelected ? 'link' : 'neutral',
    icon: subNavId ? /*#__PURE__*/React.createElement(Icon, {
      glyph: "next",
      label: false
    }) : null,
    "aria-expanded": !subNavId ? undefined : isSelected,
    "aria-haspopup": !!subNavId,
    onClick: onSelect ? onClick : undefined,
    onKeyDown: subNavId ? onKeyDown : undefined,
    onMouseOver: enableMouseOver ? onMouseOver : undefined
  }), navItem.title, isSelected && renderAimArea());
  /* eslint-enable jsx-a11y/mouse-events-have-key-events */
});
export const BrowseNavItem = menuAim({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTriggerNode: component => component.getTriggerNode(),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getMenuNode: component => component.getMenuNode(),
  onMouseLeave: (component, mousePos) => component.onMouseLeave(mousePos)
  // @ts-expect-error TS doesn't like this
})(InternalBrowseNavItem);
//# sourceMappingURL=browse-nav.react-component.js.map